import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";

const spaces = [
  { id: "kitchen", title: "Kitchen", description: "A fresh start for the heart of the home." },
  { id: "dining", title: "Dining area", description: "Room to relax, gather, and enjoy your space." },
  { id: "hallway", title: "Hallway", description: "A welcoming finish, from one room to the next." },
];

export default function BeforeAfter() {
  const [selected, setSelected] = useState(spaces[0]);
  return (
    <section id="our-work" aria-labelledby="results-heading" className="py-20 md:py-28 bg-slate-50 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Our work · Before & after</p>
            <h2 id="results-heading" className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-slate-900">Real spaces. <span className="font-medium">A visible difference.</span></h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Take a closer look at our cleaning work, with real photos from before and after our visit.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 text-blue-700 font-semibold text-sm py-3 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700">Get your free quote <ArrowUpRight size={18} aria-hidden="true" /></Link>
        </div>

        <div className="flex flex-wrap gap-2 mt-8 mb-6" role="group" aria-label="Choose a space to compare">
          {spaces.map(space => (
            <button key={space.id} type="button" aria-pressed={selected.id === space.id} onClick={() => setSelected(space)} className={`min-h-11 px-5 py-3 rounded-full text-sm font-semibold border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 ${selected.id === space.id ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-800"}`}>{space.title}</button>
          ))}
        </div>

        <div className="rounded-2xl sm:rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-xl shadow-slate-200/40">
          <div className="grid grid-cols-2 gap-1 sm:gap-2 p-1 sm:p-2" aria-label={`${selected.title} before and after cleaning`}>
            {["before", "after"].map(stage => (
              <figure key={`${selected.id}-${stage}`} className="relative m-0 overflow-hidden rounded-xl sm:rounded-2xl bg-slate-100">
                <img src={`${process.env.PUBLIC_URL || ""}/images/results/${selected.id}-${stage}.webp`} alt={`${selected.title} ${stage} cleaning by M Brothers`} width="960" height="1280" loading="lazy" decoding="async" className="w-full aspect-[3/4] max-h-[560px] object-contain" />
                <figcaption className={`absolute top-3 left-3 sm:top-5 sm:left-5 inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold shadow-sm ${stage === "after" ? "bg-blue-900 text-white" : "bg-white text-slate-800"}`}>
                  {stage === "after" && <Check size={14} aria-hidden="true" />}{stage === "before" ? "Before" : "After"}
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="px-5 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2" aria-live="polite">
            <div><h3 className="font-display text-xl font-medium text-slate-900">{selected.title}</h3><p className="mt-1 text-sm text-slate-600">{selected.description}</p></div>
            <p className="text-xs text-slate-500">Actual project photos · Different camera angles</p>
          </div>
        </div>
      </div>
    </section>
  );
}
